import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Brain, Palette, Megaphone, Globe, Users, Calendar,
  ShoppingBag, Bot, BarChart2, ArrowRight, Share2,
  Shield, Zap, Check, ChevronDown, ChevronUp,
  Image, Mail, TrendingUp, Video, Play, BarChart,
  ChevronLeft, ChevronRight, Layers, Trophy,
  Sprout, Leaf, TreePine, Rocket, Crown,
  Unlink, Clock, X as XIcon,
} from 'lucide-react';
import { PRICING_PLANS } from '../../constants/pricing';
import './Homepage.css';

// ── Frankenstack data ─────────────────────────────────────────────────────────

const frankenTools = [
  { name: 'Mailchimp',  cat: 'Email' },
  { name: 'Buffer',     cat: 'Social' },
  { name: 'Canva Pro',  cat: 'Design' },
  { name: 'HubSpot',    cat: 'CRM' },
  { name: 'Webflow',    cat: 'Site' },
  { name: 'Calendly',   cat: 'Bookings' },
  { name: 'Stripe',     cat: 'Payments' },
  { name: 'Ahrefs',     cat: 'SEO' },
];

const painPoints = [
  {
    icon: Palette,
    title: 'Your brand fragments across every tool you add.',
    desc: 'Logo in Canva. Palette in Figma. Tone of voice in a Google Doc nobody opens. Email templates with their own typography. Every new app forces another copy of your brand guide — and quietly diverges from the last.',
  },
  {
    icon: Unlink,
    title: 'Your CRM is blind to half the customer journey.',
    desc: 'A lead opens three emails, clicks a landing page, books a call — and your CRM still flags them as cold. Zapier was supposed to bridge the silos. The bridges break, drift, or never run, and the data stays stranded.',
  },
  {
    icon: Bot,
    title: 'Generative AI accelerates the chaos — it doesn\'t resolve it.',
    desc: 'ChatGPT drafts the email. Jasper writes the blog. Canva\'s AI generates the image. Each accelerates a single task, but none of them knows your customer, your brand voice, or what the others just shipped. You\'re still orchestrating by hand.',
  },
];

// Prices verified 2026-09-09. Tiers chosen for feature parity with AiMe Growth
// (not necessarily each vendor's cheapest tier) — see the methodology note
// below the comparison table. Re-verify before reusing this table; SaaS
// pricing drifts (Ahrefs Lite was $99 as recently as this page's last price
// check and is $129 today; Canva Pro was $13, now $18).
const stackRows = [
  { cat: 'AI copywriting',   tool: 'ChatGPT Plus',           price: 20 },
  { cat: 'Email marketing',  tool: 'Mailchimp Standard',     price: 20 },
  { cat: 'Social scheduling',tool: 'Buffer Team (4 channels)',price: 44 },
  { cat: 'Image / design',   tool: 'Canva Pro',              price: 18 },
  { cat: 'Website builder',  tool: 'Squarespace Core',       price: 36 },
  { cat: 'Landing pages',    tool: 'Unbounce Optimize',      price: 145 },
  { cat: 'CRM',              tool: 'HubSpot Marketing Pro',  price: 890 },
  { cat: 'SEO research',     tool: 'Ahrefs Standard',        price: 249 },
];
const stackTotal = stackRows.reduce((s, r) => s + r.price, 0);

// ── Feature data ──────────────────────────────────────────────────────────────

const features = [
  // Row 1: Content Engine (large) + Brand Management
  {
    id: 'c1',
    icon: Brain,
    title: 'AI Content Engine',
    desc: 'Generate on-brand blog posts, ad copy, email sequences, and social content in seconds. Powered by Gemini and tuned to your brand voice — every output sounds like you, not a template.',
    accent: '#2177FF',
    large: true,
  },
  {
    id: 'c2',
    icon: Palette,
    title: 'Brand Management',
    desc: 'Upload your brand guide once. AiMe extracts your color palette, typography, and tone of voice, then applies them consistently across every asset it creates.',
    accent: '#1EACA6',
  },
  // Row 2: Multi-Variant Campaigns (large) + Scheduling
  {
    id: 'c3',
    icon: Megaphone,
    title: 'Multi-Variant Campaign Auto-Generation',
    desc: 'Generate up to 5 distinct campaign variants per asset — each with a different persuasion angle. A/B test across email, landing pages, and ad platforms, then auto-promote winners and iterate. One brief, multiple angles, autonomous scaling.',
    accent: '#7C6FFF',
    large: true,
  },
  {
    id: 'c6',
    icon: Calendar,
    title: 'Scheduling & Publishing',
    desc: 'Queue approved content to WordPress, Webflow, LinkedIn, Instagram, Facebook, and more from a unified calendar. Schedule once, publish everywhere — new CMS connectors added regularly.',
    accent: '#F59E0B',
  },
  // Row 3: Adaptive Optimization (large) + Lead Capture
  {
    id: 'c4',
    icon: Globe,
    title: 'Adaptive Website Optimization',
    desc: "It's not just launching a polished site — it's the AI continuously testing and optimizing elements in real-time. Headlines, CTAs, layouts, and copy are A/B/n tested automatically. Winning variants get more traffic, losers are replaced with fresh challengers. Infinite optimization, zero manual intervention.",
    accent: '#2177FF',
    large: true,
  },
  {
    id: 'c5',
    icon: Users,
    title: 'Lead Capture & CRM',
    desc: 'Build embeddable lead capture forms with A/B testing built in. Every submission flows straight into the integrated SuiteCRM with lead scoring and nurture workflows.',
    accent: '#1EACA6',
  },
  // Row 4: Cross-Channel + Digital Marketplace + Autonomous Ops
  {
    id: 'c6b',
    icon: Share2,
    title: 'Cross-Channel Content Cohesion',
    desc: 'One article becomes a LinkedIn thought piece, an Instagram carousel, a Twitter thread, and an email sequence — each adapted to the platform\'s native style while maintaining your brand voice. Intelligent repurposing, not copy-paste.',
    accent: '#8B5CF6',
  },
  {
    id: 'c7',
    icon: ShoppingBag,
    title: 'Digital Marketplace',
    desc: 'Sell digital downloads, video courses, and physical products directly from your AiMe-built site. Built-in payments via Stripe, secure video delivery via Bunny CDN, and full order management.',
    accent: '#EC4899',
  },
  {
    id: 'c9',
    icon: Bot,
    title: 'Autonomous Operations',
    desc: 'Configure your approval preference — full auto, review-before-publish, or anything in between. AiMe drafts content, scores leads, monitors campaigns, auto-promotes A/B test winners, and generates iterative variants around the clock.',
    accent: '#7C6FFF',
  },
  // Row 5: Predictive Analytics (large)
  {
    id: 'c8',
    icon: TrendingUp,
    title: 'Predictive Analytics & Foresight',
    desc: "AiMe doesn't just report on the past — it actively predicts future market trends, customer behaviour shifts, and optimal strategic adjustments before they become apparent. AI-powered lead scoring, weekly automated insight reports, and trend forecasting transform reactive reporting into proactive strategy.",
    accent: '#1EACA6',
    large: true,
  },
];

const steps = [
  {
    num: '01',
    title: 'Upload your brand',
    desc: "Drop in your brand guide PDF or logo. AiMe extracts your color palette, typography, and tone of voice automatically. No brand guide? AiMe can build one from scratch.",
  },
  {
    num: '02',
    title: 'Describe your goal',
    desc: "Tell AiMe what you're working toward — a product launch, lead gen campaign, or full content strategy — and it generates a structured masterplan with linked assets.",
  },
  {
    num: '03',
    title: 'Review and publish',
    desc: "Approve AI drafts, publish across WordPress, Webflow, LinkedIn, Instagram, and more from a single calendar. Set your autonomy level and let the engine keep content flowing.",
  },
];

const pricing = PRICING_PLANS;

const faqs = [
  // Trust & Control
  {
    q: 'Can I really trust an AI to run my marketing?',
    a: 'Yes. AiMe gives you full control over autonomy levels — set it to auto-publish for routine content, or require your approval before anything goes live. Every action is logged so you always know what the platform has done on your behalf.'
  },
  {
    q: 'Do I need any technical skills to use AiMe?',
    a: 'None. AiMe is designed for business owners, not marketers or developers. The onboarding wizard sets everything up in under 15 minutes — upload your logo, describe your business, and AiMe generates your brand guide, email templates, first batch of content, and a landing page automatically.'
  },
  {
    q: 'How long does it take to set up?',
    a: "About 15 minutes. The onboarding wizard asks a few questions about your business, then AiMe generates your brand guide, first batch of articles, social media posts, an email template, a lead capture quiz, and a landing page — all before you reach the dashboard. Want more customisation? AiMe's built-in AI assistant learns as you go and grows with your business — just ask it to adjust anything, anytime."
  },
  // Trial & Pricing
  {
    q: 'What does the 14-day free trial include?',
    a: 'The trial gives you full access to the Growth tier — AI content generation, brand management, campaigns, email marketing, site builder, lead capture, scheduling, CRM, analytics, and more. The only restrictions are paid third-party services: domain registration, SMS/phone numbers, and connecting external CMS sites (WordPress, Webflow, etc.).'
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. No lock-in contracts. Cancel from your billing dashboard at any time and your subscription ends at the close of the current billing period. Your data remains accessible for 30 days after cancellation.'
  },
  // Use Cases
  {
    q: 'Can AiMe handle multiple brands or clients?',
    a: 'Yes. Each project in AiMe has its own brand guide, content library, campaigns, and CRM pipeline. Agencies and multi-brand operators create a separate project per client, each with its own autonomous marketing engine.'
  },
  {
    q: 'Does AiMe work for service businesses (legal, accounting, consulting)?',
    a: 'Absolutely. AiMe was built with service businesses as a primary audience. It generates thought leadership content, manages lead nurturing sequences, and tracks prospects through the CRM — exactly what professional services firms need to attract and convert clients.'
  },
  {
    q: 'Can I connect my existing website?',
    a: 'Yes. AiMe currently connects to WordPress and Webflow, with additional CMS connectors added regularly. Once paired, you can publish articles, manage SEO metadata, and schedule content directly from AiMe to your existing site — no migration required.'
  },
  // How It Works
  {
    q: 'How does brand consistency work?',
    a: "Upload your brand guide PDF or logo once. AiMe's extraction engine analyzes your typography, color palette, and tone of voice to create a persistent Brand DNA profile. Every piece of content — from ad copy to email templates to landing pages — is generated against that profile."
  },
  {
    q: 'Do I own the content AiMe creates?',
    a: 'Yes, 100%. All content generated by AiMe belongs to you. Export articles, download images, and use generated copy anywhere — there are no licensing restrictions or usage limits on your content.'
  },
  {
    q: 'What happens during the free trial if I don\'t convert?',
    a: 'Your workspace stays intact. If you decide to subscribe later, everything you created during the trial — content, brand guides, landing pages, leads — is still there waiting for you.'
  },
  // Technical
  {
    q: 'What AI powers AiMe?',
    a: 'AiMe uses Google Gemini as its primary AI engine, with automatic fallback across model versions to ensure reliability. Image generation is powered by Gemini and Flux. All AI output is tuned to your brand voice, not generic templates.'
  },
  {
    q: 'Is my data safe?',
    a: 'Yes. AiMe runs on dedicated infrastructure with all data encrypted in transit (TLS) and at rest. Your CRM data, brand assets, and customer information are never shared with other users or used to train AI models.'
  },
  {
    q: 'What tools does AiMe integrate with?',
    a: 'AiMe integrates natively with WordPress, Webflow, LinkedIn, Instagram, Facebook, and X for publishing, with new CMS connectors added regularly. It includes a full SuiteCRM instance for lead management, Stripe for payments, Cloudflare for domains, and Bunny CDN for video delivery.'
  },
];

// ── Marketing Assessment Quiz ─────────────────────────────────────────────────

// LeadSubmissionCreate.project_id is required server-side (no default) — without it
// the /api/landing-pages/leads/submit call below 422s. fetch() doesn't reject on an
// HTTP error status and the result is never inspected, so this failed completely
// silently: the UI always shows "Setting up your account..." and redirects as if it
// worked, but no lead was ever saved. AiMe's own "AiMe" project — house account for
// AiMe's own marketing site — is the target for quiz leads captured here.
const ASSESSMENT_LEAD_PROJECT_ID = '662f356d-d7f4-4310-bd39-cc55c7c0457c';

const assessmentQuestions = [
  {
    q: 'How do you currently handle your marketing?',
    options: [
      { label: "I don't — it's on my to-do list", score: 0 },
      { label: 'I do it myself when I find time', score: 1 },
      { label: 'I have a freelancer or VA helping', score: 2 },
      { label: 'I use an agency or have a marketing hire', score: 3 },
    ],
  },
  {
    q: 'How many hours per week do you spend on marketing?',
    options: [
      { label: 'Less than 1 hour', score: 0 },
      { label: '1–5 hours', score: 1 },
      { label: '5–15 hours', score: 2 },
      { label: 'More than 15 hours', score: 3 },
    ],
  },
  {
    q: 'Which channels are you actively posting on?',
    options: [
      { label: 'None yet', score: 0 },
      { label: 'Just one (website or one social platform)', score: 1 },
      { label: '2–3 channels', score: 2 },
      { label: '4+ channels with a content calendar', score: 3 },
    ],
  },
  {
    q: "What's your biggest marketing challenge right now?",
    options: [
      { label: "I don't know where to start", score: 0 },
      { label: "I can't find the time to be consistent", score: 1 },
      { label: "I'm doing things but not seeing results", score: 2 },
      { label: "I need to scale what's already working", score: 3 },
    ],
  },
  {
    q: 'Do you have a defined brand guide (colours, fonts, tone of voice)?',
    options: [
      { label: 'No — just a logo', score: 0 },
      { label: 'Loosely — I know my colours but nothing documented', score: 1 },
      { label: 'Yes — a basic brand guide exists', score: 2 },
      { label: 'Yes — a comprehensive guide with voice, typography, and imagery', score: 3 },
    ],
  },
  {
    q: 'How do you currently capture and manage leads?',
    options: [
      { label: "I don't — leads come in via email or phone", score: 0 },
      { label: 'I have a contact form on my website', score: 1 },
      { label: 'I use a basic CRM or spreadsheet', score: 2 },
      { label: 'I have automated lead capture, scoring, and nurture workflows', score: 3 },
    ],
  },
];

// ── Dashboard Mockup Carousel ─────────────────────────────────────────────────

const slides = [
  {
    id: 'content',
    label: 'AI Content',
    icon: Brain,
    color: '#2177FF',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <Brain size={16} />
          <span>AI Content Engine</span>
          <span className="hp-slide-tag">Gemini</span>
        </div>
        <div className="hp-slide-prompt">
          <span className="hp-slide-label">Prompt</span>
          <div className="hp-slide-input">Write a LinkedIn post announcing our new product launch for sustainable packaging...</div>
        </div>
        <div className="hp-slide-output">
          <span className="hp-slide-label">Generated</span>
          <div className="hp-slide-article">
            <div className="hp-slide-line hp-line-full" />
            <div className="hp-slide-line hp-line-full" />
            <div className="hp-slide-line hp-line-3q" />
            <div className="hp-slide-line hp-line-half" />
          </div>
        </div>
        <div className="hp-slide-chips">
          <span className="hp-chip hp-chip-blue">Blog Post</span>
          <span className="hp-chip hp-chip-teal">LinkedIn</span>
          <span className="hp-chip hp-chip-purple">Ad Copy</span>
          <span className="hp-chip hp-chip-blue">Email</span>
        </div>
      </div>
    )
  },
  {
    id: 'images',
    label: 'Image Gen',
    icon: Image,
    color: '#7C6FFF',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <Image size={16} />
          <span>AI Image Generation</span>
          <span className="hp-slide-tag">Flux</span>
        </div>
        <div className="hp-slide-image-grid">
          {[
            { bg: 'linear-gradient(135deg,#2177FF22,#7C6FFF44)', label: 'Product Hero' },
            { bg: 'linear-gradient(135deg,#1EACA622,#2177FF44)', label: 'Social Square' },
            { bg: 'linear-gradient(135deg,#EC489922,#7C6FFF44)', label: 'Banner Ad' },
            { bg: 'linear-gradient(135deg,#F59E0B22,#EC489944)', label: 'Story' },
          ].map(img => (
            <div key={img.label} className="hp-slide-img-card" style={{ background: img.bg }}>
              <div className="hp-slide-img-icon"><Image size={20} style={{ opacity: 0.4 }} /></div>
              <span className="hp-slide-img-label">{img.label}</span>
            </div>
          ))}
        </div>
        <div className="hp-slide-chips">
          <span className="hp-chip hp-chip-purple">Instagram</span>
          <span className="hp-chip hp-chip-blue">Facebook</span>
          <span className="hp-chip hp-chip-teal">LinkedIn</span>
        </div>
      </div>
    )
  },
  {
    id: 'campaign',
    label: 'Campaigns',
    icon: Megaphone,
    color: '#1EACA6',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <Megaphone size={16} />
          <span>Campaign Orchestration</span>
          <span className="hp-slide-tag hp-tag-green">Active</span>
        </div>
        <div className="hp-slide-campaign">
          <div className="hp-camp-row">
            <span className="hp-camp-name">Q1 Product Launch</span>
            <span className="hp-camp-stat hp-stat-blue">8 assets</span>
          </div>
          <div className="hp-camp-channels">
            {['WP', 'LI', 'IG', 'FB', 'Email'].map(ch => (
              <span key={ch} className="hp-camp-ch">{ch}</span>
            ))}
          </div>
          <div className="hp-camp-progress">
            <div className="hp-progress-bar"><div className="hp-progress-fill" style={{ width: '68%', background: '#1EACA6' }} /></div>
            <span className="hp-progress-label">68% published</span>
          </div>
        </div>
        <div className="hp-slide-kpis">
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-blue">2.4k</span><span className="hp-kpi-lbl">Impressions</span></div>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-teal">184</span><span className="hp-kpi-lbl">Clicks</span></div>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-purple">12</span><span className="hp-kpi-lbl">Conversions</span></div>
        </div>
      </div>
    )
  },
  {
    id: 'variants',
    label: 'A/B Testing',
    icon: Layers,
    color: '#7C6FFF',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <Layers size={16} />
          <span>Multi-Variant A/B Testing</span>
          <span className="hp-slide-tag hp-tag-purple">Auto-scale</span>
        </div>
        <div className="hp-variant-grid">
          {[
            { label: 'Emotional', angle: 'Personal story hook', ctr: '4.2%', status: 'winner' },
            { label: 'Data-Driven', angle: 'Statistics-led opening', ctr: '3.1%', status: 'testing' },
            { label: 'Urgency', angle: 'Time-limited offer', ctr: '2.8%', status: 'archived' },
          ].map(v => (
            <div key={v.label} className={`hp-variant-card ${v.status === 'winner' ? 'hp-variant-winner' : v.status === 'archived' ? 'hp-variant-archived' : ''}`}>
              <div className="hp-variant-top">
                <span className="hp-variant-label">{v.label}</span>
                {v.status === 'winner' && <Trophy size={12} className="hp-variant-trophy" />}
              </div>
              <div className="hp-variant-angle">{v.angle}</div>
              <div className="hp-variant-ctr">{v.ctr} CTR</div>
            </div>
          ))}
        </div>
        <div className="hp-slide-chips">
          <span className="hp-chip hp-chip-purple">Auto-Promote</span>
          <span className="hp-chip hp-chip-blue">Iterate Winners</span>
          <span className="hp-chip hp-chip-teal">5 Angles</span>
        </div>
      </div>
    )
  },
  {
    id: 'email',
    label: 'EDMs',
    icon: Mail,
    color: '#F59E0B',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <Mail size={16} />
          <span>Email Sequences</span>
          <span className="hp-slide-tag hp-tag-amber">3 live</span>
        </div>
        <div className="hp-slide-seq">
          {[
            { step: '01', subject: 'Welcome to the future of marketing', delay: 'Immediately', open: '64%' },
            { step: '02', subject: 'Here\'s what AiMe built for your brand', delay: 'Day 2', open: '51%' },
            { step: '03', subject: 'Your first campaign is ready to launch', delay: 'Day 5', open: '43%' },
          ].map(s => (
            <div key={s.step} className="hp-seq-row">
              <span className="hp-seq-num">{s.step}</span>
              <div className="hp-seq-body">
                <div className="hp-seq-subject">{s.subject}</div>
                <div className="hp-seq-meta">{s.delay} · {s.open} open rate</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: 'leads',
    label: 'Lead Gen',
    icon: TrendingUp,
    color: '#EC4899',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <TrendingUp size={16} />
          <span>Lead Capture & CRM</span>
          <span className="hp-slide-tag hp-tag-pink">Live form</span>
        </div>
        <div className="hp-slide-kpis" style={{ marginBottom: 16 }}>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-pink">247</span><span className="hp-kpi-lbl">Leads this month</span></div>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-blue">18%</span><span className="hp-kpi-lbl">Conversion rate</span></div>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-teal">$42</span><span className="hp-kpi-lbl">CPL</span></div>
        </div>
        <div className="hp-lead-list">
          {[
            { name: 'Sarah Mitchell', score: 92, status: 'Hot' },
            { name: 'James Okafor', score: 78, status: 'Warm' },
            { name: 'Priya Desai', score: 65, status: 'Warm' },
          ].map(lead => (
            <div key={lead.name} className="hp-lead-row">
              <div className="hp-lead-avatar">{lead.name[0]}</div>
              <span className="hp-lead-name">{lead.name}</span>
              <span className="hp-lead-score">{lead.score}</span>
              <span className={`hp-lead-status hp-status-${lead.status.toLowerCase()}`}>{lead.status}</span>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: 'video',
    label: 'Video',
    icon: Video,
    color: '#7C6FFF',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <Video size={16} />
          <span>Digital Products & Video</span>
          <span className="hp-slide-tag">Bunny CDN</span>
        </div>
        <div className="hp-video-list">
          {[
            { title: 'Mastering Social Media Marketing', enrolled: 312, revenue: '$8,424', progress: 82 },
            { title: 'Email Marketing Masterclass', enrolled: 187, revenue: '$4,209', progress: 55 },
            { title: 'Brand Strategy Foundations', enrolled: 94, revenue: '$1,880', progress: 30 },
          ].map(v => (
            <div key={v.title} className="hp-video-row">
              <div className="hp-video-thumb"><Play size={14} /></div>
              <div className="hp-video-body">
                <div className="hp-video-title">{v.title}</div>
                <div className="hp-progress-bar" style={{ margin: '6px 0 4px' }}>
                  <div className="hp-progress-fill" style={{ width: `${v.progress}%`, background: '#7C6FFF' }} />
                </div>
                <div className="hp-video-meta">{v.enrolled} enrolled · {v.revenue} revenue</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart,
    color: '#1EACA6',
    render: () => (
      <div className="hp-slide-content">
        <div className="hp-slide-header">
          <BarChart size={16} />
          <span>Analytics & SEO</span>
          <span className="hp-slide-tag hp-tag-green">+24% MoM</span>
        </div>
        <div className="hp-slide-kpis" style={{ marginBottom: 16 }}>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-teal">14.2k</span><span className="hp-kpi-lbl">Sessions</span></div>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-blue">3.8%</span><span className="hp-kpi-lbl">CTR</span></div>
          <div className="hp-kpi"><span className="hp-kpi-val hp-kpi-purple">#4</span><span className="hp-kpi-lbl">Avg rank</span></div>
        </div>
        <div className="hp-chart-bars">
          {[40, 55, 48, 70, 62, 85, 78].map((h, i) => (
            <div key={i} className="hp-bar-wrap">
              <div className="hp-bar" style={{ height: `${h}%`, background: i === 5 ? '#1EACA6' : 'rgba(30,172,166,0.3)' }} />
            </div>
          ))}
        </div>
        <div className="hp-chart-labels">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(d => (
            <span key={d} className="hp-chart-label">{d}</span>
          ))}
        </div>
      </div>
    )
  },
];

const DashboardMockup: React.FC = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  const next = useCallback(() => setActive(a => (a + 1) % slides.length), []);
  const prev = useCallback(() => setActive(a => (a - 1 + slides.length) % slides.length), []);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 4000);
    return () => clearInterval(t);
  }, [paused, next]);

  const slide = slides[active];

  return (
    <div className="hp-mockup-shell" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {/* Window chrome */}
      <div className="hp-mockup-chrome">
        <div className="hp-chrome-dots">
          <span className="hp-dot hp-dot-red" />
          <span className="hp-dot hp-dot-yellow" />
          <span className="hp-dot hp-dot-green" />
        </div>
        <div className="hp-chrome-url">app.aime.ai / {slide.id}</div>
      </div>

      {/* Sidebar tabs */}
      <div className="hp-mockup-body">
        <div className="hp-mockup-sidebar">
          <div className="hp-mockup-logo"><span style={{ color: '#2177FF' }}>Ai</span>Me</div>
          {slides.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.id}
                className={`hp-mockup-nav-item ${i === active ? 'hp-nav-item-active' : ''}`}
                onClick={() => { setActive(i); setPaused(true); }}
                style={{ '--nav-color': s.color } as React.CSSProperties}
              >
                <Icon size={15} />
                <span>{s.label}</span>
              </div>
            );
          })}
        </div>

        {/* Main panel */}
        <div className="hp-mockup-main">
          <div className="hp-slide-animate" key={active}>
            {slide.render()}
          </div>
        </div>
      </div>

      {/* Carousel controls */}
      <div className="hp-mockup-controls">
        <button className="hp-ctrl-btn" onClick={prev}><ChevronLeft size={14} /></button>
        <div className="hp-ctrl-dots">
          {slides.map((_, i) => (
            <span
              key={i}
              className={`hp-ctrl-dot ${i === active ? 'hp-ctrl-dot-active' : ''}`}
              onClick={() => { setActive(i); setPaused(true); }}
            />
          ))}
        </div>
        <button className="hp-ctrl-btn" onClick={next}><ChevronRight size={14} /></button>
      </div>
    </div>
  );
};

// ── Component ─────────────────────────────────────────────────────────────────

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const [navScrolled, setNavScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  // Assessment quiz state
  const [assessmentStep, setAssessmentStep] = useState(0);
  const [assessmentAnswers, setAssessmentAnswers] = useState<number[]>([]);
  const [assessmentEmail, setAssessmentEmail] = useState('');
  const [revealPhase, setRevealPhase] = useState(0); // 0=not started, 1-5=sweeping through tiers, 6=done
  const [assessmentSubmitted, setAssessmentSubmitted] = useState(false);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="hp-root">

      {/* ── Nav ───────────────────────────────────────────────────────────── */}
      <nav className={`hp-nav ${navScrolled ? 'hp-nav-scrolled' : ''}`}>
        <div className="hp-nav-inner">
          <div className="hp-logo">
            <span className="hp-logo-ai">Ai</span>Me
          </div>
          <div className="hp-nav-links">
            <a href="#features" className="hp-nav-link">Features</a>
            <a href="#how" className="hp-nav-link">How it works</a>
            <a href="#pricing" className="hp-nav-link">Pricing</a>
            <a href="/support" className="hp-nav-link">Support</a>
          </div>
          <div className="hp-nav-actions">
            <button className="hp-btn hp-btn-ghost" onClick={() => navigate('/login')}>Login</button>
            <button className="hp-btn hp-btn-primary" onClick={() => navigate('/register')}>Get Started</button>
          </div>
        </div>
      </nav>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="hp-hero">
        <div className="hp-hero-bg">
          <div className="hp-mesh hp-mesh-1" />
          <div className="hp-mesh hp-mesh-2" />
          <div className="hp-mesh hp-mesh-3" />
          <div className="hp-grid-dots" />
        </div>

        <div className="hp-hero-content">
          <div className="hp-badge">
            <Zap size={14} fill="currentColor" />
            Next-Gen Autonomous Marketing
          </div>
          <h1 className="hp-h1">
            Your Entire Marketing Team, <br />
            <span className="hp-gradient-text">Powered by AI.</span>
          </h1>
          <p className="hp-hero-sub">
            Stop stitching together generic tools. AiMe is your autonomous marketing operator — managing content, campaigns, sites, and leads from a single intelligent platform.
          </p>
          <div className="hp-hero-actions">
            <button className="hp-btn hp-btn-primary" onClick={() => navigate('/register')}>
              Start Free Trial <ArrowRight size={16} />
            </button>
            <button className="hp-btn hp-btn-secondary" onClick={() => navigate('/login')}>
              Sign In
            </button>
          </div>
          <p className="hp-hero-note">14-day free trial · No credit card required</p>

          <div className="hp-hero-mockup">
            <div className="hp-mockup-wrap">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* ── Trust Strip ───────────────────────────────────────────────────── */}
      <section className="hp-trust">
        <div className="hp-container">
          <div className="hp-trust-inner">
            <div className="hp-trust-logo"><Brain size={18} /> Gemini</div>
            <div className="hp-trust-logo"><Globe size={18} /> WordPress</div>
            <div className="hp-trust-logo"><Layers size={18} /> Webflow</div>
            <div className="hp-trust-logo"><Shield size={18} /> Cloudflare</div>
            <div className="hp-trust-logo"><ShoppingBag size={18} /> Stripe</div>
            <div className="hp-trust-logo"><Palette size={18} /> Flux</div>
          </div>
        </div>
      </section>

      {/* ── The Frankenstack ─────────────────────────────────────────────── */}
      <section className="hp-section hp-franken" id="problem">
        <div className="hp-centered">
          <span className="hp-eyebrow">The Problem</span>
          <h2 className="hp-h2">Most marketing stacks are held together by <span className="hp-gradient-text">duct tape.</span></h2>
          <p className="hp-hero-sub">
            Mailchimp, Buffer, Canva, HubSpot, Webflow, Calendly, Stripe, Ahrefs — eight subscriptions, eight logins, none of them sharing data. Generative AI helps you write faster, but it doesn't wire those tools together, sync your brand across them, or chase leads across the silos in between. Whether you're untangling a frankenstack today or facing one tomorrow, the symptoms are identical: <strong>fragmented coverage, disjointed data, manual orchestration.</strong>
          </p>
          <p className="hp-hero-sub" style={{ marginTop: 24 }}>
            <strong>AiMe orchestrates the entire marketing function — content, campaigns, sites, leads — as a single always-on team built for SMEs.</strong>
          </p>
        </div>

        {/* Tool tangle visual */}
        <div className="hp-franken-visual">
          <div className="hp-franken-stack">
            <span className="hp-franken-label">The typical stack</span>
            <div className="hp-franken-tools">
              {frankenTools.map(t => (
                <div key={t.name} className="hp-franken-pill">
                  <span className="hp-franken-pill-name">{t.name}</span>
                  <span className="hp-franken-pill-cat">{t.cat}</span>
                </div>
              ))}
            </div>
            <div className="hp-franken-stat">
              <Clock size={14} /> Hours/week stitching them together
            </div>
          </div>

          <div className="hp-franken-arrow" aria-hidden="true">→</div>

          <div className="hp-franken-aime">
            <span className="hp-franken-label">With AiMe</span>
            <div className="hp-franken-aime-card">
              <div className="hp-logo" style={{ fontSize: 36 }}>
                <span className="hp-logo-ai">Ai</span>Me
              </div>
              <p className="hp-franken-aime-desc">
                One platform.<br />One brand.<br />One bill.
              </p>
            </div>
            <div className="hp-franken-stat hp-franken-stat-good">
              <Check size={14} /> Runs itself
            </div>
          </div>
        </div>

        {/* Pain points */}
        <div className="hp-pain-grid">
          {painPoints.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="hp-pain-card">
                <div className="hp-pain-icon"><Icon size={22} /></div>
                <h3 className="hp-pain-title">{p.title}</h3>
                <p className="hp-pain-desc">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Comparison: Replace your stack ───────────────────────────────── */}
      <section className="hp-section hp-compare" id="compare">
        <div className="hp-centered">
          <span className="hp-eyebrow">The Math</span>
          <h2 className="hp-h2">Replace your stack. <span className="hp-gradient-text">Cut your bill by 80%.</span></h2>
          <p className="hp-hero-sub">A typical SMB marketing setup vs. AiMe Growth.</p>
        </div>

        <div className="hp-compare-grid">
          {/* Left: typical stack */}
          <div className="hp-compare-col hp-compare-col-bad">
            <div className="hp-compare-col-head">
              <h3 className="hp-h3">The Frankenstack</h3>
              <p className="hp-compare-sub">Average SMB marketing setup</p>
            </div>
            <ul className="hp-compare-rows">
              {stackRows.map(r => (
                <li key={r.cat} className="hp-compare-row">
                  <span className="hp-compare-cat">{r.cat}</span>
                  <span className="hp-compare-tool">{r.tool}</span>
                  <span className="hp-compare-price">${r.price}/mo</span>
                </li>
              ))}
              <li className="hp-compare-row hp-compare-extra">
                <span className="hp-compare-cat">Glue & maintenance</span>
                <span className="hp-compare-tool">Zapier + freelancer time</span>
                <span className="hp-compare-price">+ unknown</span>
              </li>
            </ul>
            <div className="hp-compare-total hp-compare-total-bad">
              <span>Total</span>
              <span className="hp-compare-total-amount">${stackTotal}+/mo</span>
            </div>
            <ul className="hp-compare-cons">
              <li><XIcon size={14} /> 8 separate subscriptions to manage</li>
              <li><XIcon size={14} /> Brand guide scattered across tools</li>
              <li><XIcon size={14} /> No shared customer view</li>
              <li><XIcon size={14} /> You operate it all manually</li>
            </ul>
          </div>

          {/* Right: AiMe */}
          <div className="hp-compare-col hp-compare-col-good">
            <div className="hp-compare-col-head">
              <h3 className="hp-h3">AiMe Growth</h3>
              <p className="hp-compare-sub">One autonomous platform</p>
            </div>
            <ul className="hp-compare-rows">
              {stackRows.map(r => (
                <li key={r.cat} className="hp-compare-row">
                  <span className="hp-compare-cat">{r.cat}</span>
                  <span className="hp-compare-tool"><Check size={14} color="var(--hp-primary)" /> Included</span>
                  <span className="hp-compare-price">—</span>
                </li>
              ))}
              <li className="hp-compare-row hp-compare-extra">
                <span className="hp-compare-cat">Autonomous operation</span>
                <span className="hp-compare-tool"><Check size={14} color="var(--hp-primary)" /> Included</span>
                <span className="hp-compare-price">—</span>
              </li>
            </ul>
            <div className="hp-compare-total hp-compare-total-good">
              <span>Total</span>
              <span className="hp-compare-total-amount">$249/mo</span>
            </div>
            <ul className="hp-compare-cons hp-compare-pros">
              <li><Check size={14} /> One subscription, one login</li>
              <li><Check size={14} /> Single source of brand truth</li>
              <li><Check size={14} /> Unified CRM + content + analytics</li>
              <li><Check size={14} /> Runs the work for you</li>
            </ul>
            <button className="hp-btn hp-btn-primary hp-compare-cta" onClick={() => navigate('/register')}>
              Start Free Trial <ArrowRight size={16} />
            </button>
          </div>
        </div>

        <p className="hp-compare-note">
          Prices reflect the plan tier each vendor requires to match AiMe Growth's feature set (CRM lead scoring &amp; nurture, A/B-tested landing pages, full-site SEO audits) — not necessarily each vendor's cheapest tier. Verified against vendor pricing pages as of September 2026. Your actual stack may cost more — most SMBs we talk to are running 10–14 paid tools, not 8.
        </p>
      </section>

      {/* ── Features Bento ────────────────────────────────────────────────── */}
      <section className="hp-section" id="features">
        <div className="hp-centered">
          <span className="hp-eyebrow">The Platform</span>
          <h2 className="hp-h2">Everything you need to grow.</h2>
        </div>
        <div className="hp-features-grid">
          {features.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.id} className={`hp-bento-item ${f.large ? 'hp-bento-lg' : ''}`}>
                <div className="hp-bento-icon"><Icon size={24} /></div>
                <h3 className="hp-h3">{f.title}</h3>
                <p className="hp-bento-desc">{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── How it Works ─────────────────────────────────────────────────── */}
      <section className="hp-section" id="how" style={{ borderTop: '1px solid var(--hp-glass-border)' }}>
        <div className="hp-centered">
          <span className="hp-eyebrow">Simple by design</span>
          <h2 className="hp-h2">Up and running in minutes</h2>
        </div>
        <div className="hp-features-grid" style={{ marginTop: 60 }}>
          {steps.map((step) => (
            <div key={step.num} className="hp-bento-item">
              <div className="hp-badge" style={{ marginBottom: 20 }}>{step.num}</div>
              <h3 className="hp-h3">{step.title}</h3>
              <p className="hp-bento-desc">{step.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing ────────────────────────────────────────────────────── */}
      <section className="hp-section" id="pricing">
        <div className="hp-centered">
          <span className="hp-eyebrow">Simple Pricing</span>
          <h2 className="hp-h2">Plans that grow with you.</h2>
          <p className="hp-hero-sub" style={{ marginTop: 12 }}>Start free for 14 days. No credit card required.</p>
        </div>
        <div className="hp-pricing-grid">
          {pricing.map(p => (
            <div key={p.name} className={`hp-price-card ${p.featured ? 'hp-price-card-featured' : ''}`}>
              {p.featured && <div className="hp-badge" style={{ marginBottom: 16 }}>Most Popular</div>}
              <h3 className="hp-h3">{p.name}</h3>
              <div className="hp-price-amount">{p.price}<span className="hp-price-period">/mo</span></div>
              <p className="hp-hero-sub" style={{ margin: '0 0 8px', fontSize: 14 }}>{p.desc}</p>
              {p.trial && <p className="hp-price-trial">{p.trial}</p>}
              <ul className="hp-price-features">
                {p.features.map(f => (
                  <li key={f} className="hp-price-feature">
                    <Check size={16} color="var(--hp-primary)" /> {f}
                  </li>
                ))}
              </ul>
              <button className={`hp-btn ${p.featured ? 'hp-btn-primary' : 'hp-btn-secondary'}`}
                style={{ marginTop: 40, width: '100%', justifyContent: 'center' }}
                onClick={() => navigate('/register')}
              >
                {p.trial ? 'Start Free Trial' : `Choose ${p.name}`}
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ── FAQ ────────────────────────────────────────────────────────── */}
      <section className="hp-section">
        <div className="hp-centered">
          <span className="hp-eyebrow">Common Questions</span>
          <h2 className="hp-h2">Frequently Asked</h2>
        </div>
        <div className="hp-faq-list">
          {faqs.map((faq, i) => (
            <div key={i} className="hp-faq-item" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
              <div className="hp-faq-q">
                {faq.q}
                {openFaq === i ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {openFaq === i && <div className="hp-faq-a">{faq.a}</div>}
            </div>
          ))}
        </div>
      </section>

      {/* ── Marketing Assessment Quiz ──────────────────────────────────────── */}
      <section className="hp-section" id="assessment">
        <div className="hp-centered">
          <span className="hp-eyebrow">Free Assessment</span>
          <h2 className="hp-h2">Where are you on your marketing journey?</h2>
          <p className="hp-hero-sub" style={{ maxWidth: 600 }}>
            Take our 60-second marketing health check. Get a personalised score and actionable recommendations — no signup required to start.
          </p>
        </div>
        <div className="hp-assessment-container">
          <div className="hp-assessment-card">
            <div className="hp-assessment-questions">
              {assessmentStep < assessmentQuestions.length ? (
                <>
                  <div className="hp-assessment-progress">
                    <div className="hp-assessment-progress-fill" style={{ width: `${((assessmentStep + 1) / assessmentQuestions.length) * 100}%` }} />
                  </div>
                  <p className="hp-assessment-step">Question {assessmentStep + 1} of {assessmentQuestions.length}</p>
                  <h3 className="hp-h3" style={{ marginBottom: 20 }}>{assessmentQuestions[assessmentStep].q}</h3>
                  <div className="hp-assessment-options">
                    {assessmentQuestions[assessmentStep].options.map((opt, i) => (
                      <button
                        key={i}
                        className="hp-assessment-option"
                        onClick={() => {
                          const newAnswers = [...assessmentAnswers];
                          newAnswers[assessmentStep] = opt.score;
                          setAssessmentAnswers(newAnswers);
                          if (assessmentStep < assessmentQuestions.length - 1) {
                            setAssessmentStep(assessmentStep + 1);
                          } else {
                            setAssessmentStep(assessmentQuestions.length);
                          }
                        }}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {(() => {
                    const total = assessmentAnswers.reduce((a, b) => a + b, 0);
                    const maxScore = assessmentQuestions.length * 3;
                    const pct = Math.round((total / maxScore) * 100);

                    const TierIcons = [Sprout, Leaf, TreePine, Rocket, Crown];
                    const tiers = [
                      { label: 'Seedling', title: 'Getting Started', color: '#2177FF', plan: 'growth',
                        desc: "You're at the perfect starting point. AiMe builds your entire marketing engine from scratch — brand, content, campaigns, leads — in minutes." },
                      { label: 'Sprouting', title: 'Building Foundations', color: '#1EACA6', plan: 'growth',
                        desc: "You've taken the first steps. AiMe accelerates your momentum by automating the fundamentals — brand consistency, content creation, and lead capture." },
                      { label: 'Growing', title: 'Growing but Stretched', color: '#F59E0B', plan: 'growth',
                        desc: "You're doing the right things but spreading yourself thin. AiMe automates the execution so you can focus on what moves the needle." },
                      { label: 'Launching', title: 'Ready to Scale', color: '#8B5CF6', plan: 'pro',
                        desc: "You've built a solid foundation. AiMe takes what's working and scales it autonomously — predictive analytics, A/B optimization, and multi-channel campaigns on autopilot." },
                      { label: 'Stellar', title: 'Marketing Powerhouse', color: '#10b981', plan: 'enterprise',
                        desc: "You're operating at a high level. AiMe's enterprise features — adaptive optimization, predictive foresight, and autonomous scaling — unlock the next gear." },
                    ];

                    const tierIndex = pct >= 80 ? 4 : pct >= 60 ? 3 : pct >= 40 ? 2 : pct >= 20 ? 1 : 0;
                    const finalTier = tiers[tierIndex];

                    // Start sweep animation
                    if (revealPhase === 0) {
                      setTimeout(() => setRevealPhase(1), 300);
                    }
                    if (revealPhase > 0 && revealPhase <= tierIndex) {
                      setTimeout(() => setRevealPhase(revealPhase + 1), 500);
                    }
                    const sweepDone = revealPhase > tierIndex;
                    const currentSweepIndex = Math.min(Math.max(revealPhase - 1, 0), 4);
                    const displayColor = sweepDone ? finalTier.color : tiers[currentSweepIndex].color;
                    const DisplayIcon = sweepDone ? TierIcons[tierIndex] : TierIcons[currentSweepIndex];

                    return (
                      <div className="hp-assessment-result">
                        {/* Central icon with sweep ring */}
                        <div className="hp-reveal-center">
                          <div className={`hp-reveal-ring ${sweepDone ? 'hp-reveal-ring-done' : 'hp-reveal-ring-sweeping'}`} style={{ borderColor: displayColor, boxShadow: `0 0 30px ${displayColor}40` }}>
                            <div className={`hp-reveal-icon ${sweepDone ? 'hp-reveal-icon-done' : ''}`} style={{ color: displayColor }}>
                              <DisplayIcon size={48} strokeWidth={1.5} />
                            </div>
                          </div>
                          {/* Small tier dots below the ring */}
                          <div className="hp-reveal-dots">
                            {tiers.map((t, i) => (
                              <div
                                key={i}
                                className={`hp-reveal-dot ${i <= currentSweepIndex && revealPhase > 0 ? 'hp-reveal-dot-active' : ''} ${sweepDone && i === tierIndex ? 'hp-reveal-dot-final' : ''}`}
                                style={{ background: i <= currentSweepIndex && revealPhase > 0 ? t.color : undefined }}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Result text — fades in after sweep */}
                        <div className={`hp-tier-result ${sweepDone ? 'hp-tier-result-visible' : ''}`}>
                          <h3 className="hp-h3" style={{ color: finalTier.color, fontSize: '1.5rem' }}>{finalTier.title}</h3>
                          <p className="hp-bento-desc" style={{ maxWidth: 500, margin: '12px auto 0' }}>{finalTier.desc}</p>

                          <div className="hp-tier-plan-badge" style={{ borderColor: finalTier.color, color: finalTier.color }}>
                            Recommended: {finalTier.plan === 'enterprise' ? 'Enterprise' : finalTier.plan === 'pro' ? 'Pro' : 'Growth'} Plan
                          </div>

                          {!assessmentSubmitted ? (
                            <div className="hp-assessment-cta" style={{ marginTop: 24 }}>
                              <input
                                type="email"
                                className="hp-assessment-email"
                                placeholder="Enter your email to get started"
                                value={assessmentEmail}
                                onChange={e => setAssessmentEmail(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter' && assessmentEmail) { e.preventDefault(); document.getElementById('hp-assessment-submit')?.click(); }}}
                              />
                              <button
                                id="hp-assessment-submit"
                                className="hp-btn hp-btn-primary"
                                onClick={() => {
                                  if (!assessmentEmail) return;
                                  fetch('/api/landing-pages/leads/submit', {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      project_id: ASSESSMENT_LEAD_PROJECT_ID,
                                      email: assessmentEmail,
                                      source_widget: 'quiz',
                                      source_url: window.location.href,
                                      custom_fields: {
                                        assessment_score: pct,
                                        assessment_level: finalTier.title,
                                        assessment_tier: finalTier.label,
                                        recommended_plan: finalTier.plan,
                                        answers: assessmentAnswers,
                                      },
                                    }),
                                  }).catch(() => {});
                                  setAssessmentSubmitted(true);
                                  setTimeout(() => navigate(`/register?plan=${finalTier.plan}&email=${encodeURIComponent(assessmentEmail)}`), 1500);
                                }}
                              >
                                Let's Go <ArrowRight size={16} />
                              </button>
                            </div>
                          ) : (
                            <div className="hp-assessment-submitted">
                              <Check size={24} /> Setting up your account...
                            </div>
                          )}

                          <button className="hp-assessment-restart" onClick={() => { setAssessmentStep(0); setAssessmentAnswers([]); setAssessmentEmail(''); setRevealPhase(0); setAssessmentSubmitted(false); }}>
                            Retake Assessment
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ── Final CTA ─────────────────────────────────────────────────────── */}
      <section className="hp-section hp-centered" style={{ background: 'rgba(33, 119, 255, 0.05)', borderRadius: 40, margin: '80px auto' }}>
        <h2 className="hp-h2">Ready to retire your frankenstack?</h2>
        <p className="hp-hero-sub">AiMe is the autonomous marketing team built for SMEs — one platform, no duct tape.</p>
        <p className="hp-hero-note" style={{ marginTop: 0 }}>14-day free trial · No credit card required</p>
        <div className="hp-hero-actions" style={{ justifyContent: 'center' }}>
          <button className="hp-btn hp-btn-primary" style={{ padding: '16px 40px', fontSize: 16 }} onClick={() => navigate('/register')}>
            Start Your Free Trial <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      <footer className="hp-footer">
        <div className="hp-footer-inner">
          <div className="hp-logo">
            <span className="hp-logo-ai">Ai</span>Me
          </div>
          <div className="hp-nav-links">
            <a href="/terms" className="hp-nav-link">Terms</a>
            <a href="/privacy" className="hp-nav-link">Privacy</a>
            <a href="/support" className="hp-nav-link">Support</a>
          </div>
          <p className="hp-footer-copy">© {new Date().getFullYear()} ClearByte.AU. All rights reserved.</p>
        </div>
      </footer>

    </div>
  );
};

export default Homepage;
